import * as React from "react";
import ProjectsSummaryTable from "../components/ProjectsSummaryTable";
import {
  AppBar,
  Toolbar,
  TextField,
  Menu,
  MenuItem,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { dialogBoxRef } from "../../base/components/ContentRouter";
import ProjectGanttChart from "../components/GanttChart/ProjectGanttChart";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ProjectSummaryPDFPreview from "../components/ProjectsSummaryPDFPreview";
import EditableProjectTeam from "../../editing/ProjectTeamEditing";
import ProjectPC1Upload from "../../editing/ProjectPC1Upload";

interface Row {
  [key: string]: any;
}

interface IProps {
  data: Row[];
  isEditingEnable: Boolean;
  handleEditing?: any;
  region?: string;
}

const ProjectSummaryContainer = (props: IProps) => {
  const containerHeight = useSelector(
    (state: any) => state.base.contentDivHeight,
  );
  const [selectedRow, setSelectedRow] = React.useState<any>();
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [selectedRegion, setSelectedRegion] = React.useState<string>("");
  const [selectedDonor, setSelectedDonor] = React.useState<string>("");
  const [regionAnchorEl, setRegionAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [donorAnchorEl, setDonorAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [openPDFPreview, setOpenPDFPreview] = React.useState(false);
  const handleClosePDFPreview = () => {
    setOpenPDFPreview(false);
  };
  const handlePDFPreview = () => {
    setOpenPDFPreview(true);
  };
  const handleDownloadExcel = () => {
    const sortedData = [...props.data].sort((a, b) => {
      if (a.region < b.region) return -1;
      if (a.region > b.region) return 1;
      return 0;
    });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(sortedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "projects_summary.xlsx");
  };

  // const handleProjectDetails = () => {
  //     if (selectedRow) {
  //         // navigate(`/projectInfo?id=${selectedRow?.id}`);
  //         dialogBoxRef?.current?.setContent(<><ProjectContents pid={selectedRow.id} showRightMenuBar={false}/></>);
  //         dialogBoxRef?.current?.openDialog("Project Information");
  //
  //     } else {
  //         alert("Please select a row");
  //     }
  // };

  const handleGanttChart = () => {
    if (selectedRow) {
      dialogBoxRef?.current?.setContent(
        <>
          <ProjectGanttChart pId={selectedRow.id} />
        </>,
      );
      dialogBoxRef?.current?.openDialog("Project Gantt Chart");
    } else {
      alert("Please select a row");
    }
  };

  const handleSelectedRow = (selectedRow: any) => {
    setSelectedRow(selectedRow);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleRegionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRegionAnchorEl(event.currentTarget);
  };

  const handleRegionClose = (
    event: React.MouseEvent<HTMLElement>,
    region: string,
  ) => {
    setSelectedRegion(region);
    setRegionAnchorEl(null);
  };

  const handleDonorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDonorAnchorEl(event.currentTarget);
  };

  const handleDonorClose = (
    event: React.MouseEvent<HTMLElement>,
    donor: string,
  ) => {
    setSelectedDonor(donor);
    setDonorAnchorEl(null);
  };

  const filteredData = props.data
    .filter(
      (row) =>
        row.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        (selectedRegion === "" ||
          row.region === selectedRegion ||
          row.sub_regions === selectedRegion) &&
        (selectedDonor === "" ||
          row.dev_partner.toLowerCase().includes(selectedDonor.toLowerCase())),
    )
    .sort((a, b) => {
      if (!props.region) return 0;

      const aMatch = a.region === props.region;

      const bMatch = b.region === props.region;

      return Number(bMatch) - Number(aMatch);
    });

  const handleTeamEditing = (selectedRow: any) => {
    if (selectedRow) {
      // console.log("Selected Row", selectedRow)
      dialogBoxRef?.current?.setContent(
        <>
          <EditableProjectTeam pid={selectedRow.id} />
        </>,
      );
      dialogBoxRef?.current?.openDialog("Project Summary Editor");
    } else {
      alert("Please select a row");
    }
  };

  const handleUploadPC1 = (selectedRow: any) => {
    if (selectedRow) {
      // console.log("Selected Row", selectedRow)
      dialogBoxRef?.current?.setContent(
        <>
          <ProjectPC1Upload pid={selectedRow.id} />
        </>,
      );
      dialogBoxRef?.current?.openDialog("Project Summary Editor");
    } else {
      alert("Please select a row");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: containerHeight,
        padding: "10px",
      }}
    >
      <AppBar position="static">
        <Toolbar style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {/*<Button variant={"contained"} color="secondary" onClick={handleProjectDetails}>Project*/}
            {/*    Details</Button>*/}
            <Button
              variant={"contained"}
              color="secondary"
              onClick={handleGanttChart}
            >
              Gantt Chart
            </Button>
            <Button
              variant={"contained"}
              color="secondary"
              onClick={handleDownloadExcel}
            >
              Excel Download
            </Button>
            <Button
              variant={"contained"}
              color="secondary"
              onClick={handlePDFPreview}
            >
              PDF Preview
            </Button>
            {props.isEditingEnable && (
              <>
                <Button
                  variant={"contained"}
                  color="secondary"
                  onClick={() => props.handleEditing(selectedRow)}
                >
                  Edit Project
                </Button>
                <Button
                  variant={"contained"}
                  color="secondary"
                  onClick={() => handleTeamEditing(selectedRow)}
                >
                  Edit Project Team
                </Button>
                <Button
                  variant={"contained"}
                  color="secondary"
                  onClick={() => handleUploadPC1(selectedRow)}
                >
                  Upload PC1
                </Button>
              </>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant={"contained"}
              color="secondary"
              onClick={handleRegionClick}
              style={{ marginRight: "10px", minWidth: "130px" }}
            >
              {(selectedRegion === "Federal"
                ? "Cross-Provincial"
                : selectedRegion) || "Select Region"}
            </Button>
            <Menu
              anchorEl={regionAnchorEl}
              open={Boolean(regionAnchorEl)}
              onClose={() => setRegionAnchorEl(null)}
            >
              <MenuItem onClick={(event) => handleRegionClose(event, "")}>
                All Regions
              </MenuItem>
              <MenuItem
                onClick={(event) => handleRegionClose(event, "Federal")}
              >
                Cross-Provincial
              </MenuItem>
              <MenuItem
                onClick={(event) => handleRegionClose(event, "Balochistan")}
              >
                Balochistan
              </MenuItem>
              <MenuItem onClick={(event) => handleRegionClose(event, "Sindh")}>
                Sindh
              </MenuItem>
              <MenuItem onClick={(event) => handleRegionClose(event, "Punjab")}>
                Punjab
              </MenuItem>
              <MenuItem
                onClick={(event) =>
                  handleRegionClose(event, "Khyber Pakhtunkhwa")
                }
              >
                Khyber Pakhtunkhwa
              </MenuItem>
            </Menu>
            <TextField
              variant="outlined"
              placeholder="Search by title"
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                style: { color: "black", height: "40px", width: "200px" },
              }}
              style={{
                backgroundColor: "white",
                borderRadius: "4px",
                marginRight: "10px",
              }}
            />
            <Button
              variant={"contained"}
              color="secondary"
              onClick={handleDonorClick}
              style={{ minWidth: "130px" }}
            >
              {selectedDonor || "Select Donor"}
            </Button>
            <Menu
              anchorEl={donorAnchorEl}
              open={Boolean(donorAnchorEl)}
              onClose={() => setDonorAnchorEl(null)}
            >
              <MenuItem onClick={(event) => handleDonorClose(event, "")}>
                All Donors
              </MenuItem>
              <MenuItem onClick={(event) => handleDonorClose(event, "ADB")}>
                ADB
              </MenuItem>
              <MenuItem
                onClick={(event) => handleDonorClose(event, "World Bank")}
              >
                World Bank
              </MenuItem>
              <MenuItem onClick={(event) => handleDonorClose(event, "IsDB")}>
                IsDB
              </MenuItem>
              <MenuItem onClick={(event) => handleDonorClose(event, "NDRMF")}>
                NDRMF
              </MenuItem>
              <MenuItem onClick={(event) => handleDonorClose(event, "IDA")}>
                IDA
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <div style={{ flex: 1, overflow: "auto" }}>
        <ProjectsSummaryTable
          data={filteredData}
          width={"100%"}
          selectedRowFunc={handleSelectedRow}
        />
      </div>
      {/*for PDF Preview */}
      <ProjectSummaryPDFPreview
        data={filteredData}
        openPDFPreview={openPDFPreview}
        handleClosePDFPreview={handleClosePDFPreview}
      />
    </div>
  );
};

export default ProjectSummaryContainer;
